import React, { useEffect, useState } from 'react';
import Select from 'react-select';
import { useTranslation } from 'react-i18next';
import { JOB_PORTAL_KEYS, NS } from '../../../../i18n/constants/keys';
import { Button } from '../../../../ui';
import { CustomSelect } from '../../../../ui/custom_react_select/custom_select';
import { getJobFilters } from '../../../../utils/api_service/JobPosting';
import { FilterOption } from '../../../../utils/types/JobOpenings';
import { getOrganizationTree } from '../../../../utils/api_service/organization_unit';
import { FilterState } from '../../../../utils/types/JobOpenings';
import { SelectOptions } from '../../../../utils/types/react_select_types';

type FilterProps = {
  filters: FilterState;
  setFilters: React.Dispatch<React.SetStateAction<FilterState>>;
  setSearch: (value: boolean) => void;
  search: boolean;
};

const JobPostFilter = ({
  filters,
  setFilters,
  setSearch,
  search,
}: FilterProps) => {
  const { t } = useTranslation(NS.jobPortal);
  const [departmentTypeFilter, setDepartmentTypeFilter] = useState<SelectOptions[]>([]);
  const [locations, setLocations] = useState<SelectOptions[]>([]);
  const [jobTitles, setJobTitles] = useState<SelectOptions[]>([]);

  const workTypes: SelectOptions[] = [
    { label: t(JOB_PORTAL_KEYS.filter.hybrid), value: 'Hybrid' },
    { label: t(JOB_PORTAL_KEYS.filter.remote), value: 'Remote' },
    { label: t(JOB_PORTAL_KEYS.filter.office), value: 'Office' },
  ];

  const selectStyles = {
    control: (base: any, state: any) => ({
      ...base,
      minHeight: '40px',
      borderRadius: '8px',
      borderColor: state.isFocused ? '#10b981' : '#dbe3ef',
      boxShadow: state.isFocused ? '0 0 0 3px rgba(16, 185, 129, 0.12)' : 'none',
      '&:hover': {
        borderColor: state.isFocused ? '#10b981' : '#cbd5e1',
      },
    }),
    placeholder: (base: any) => ({
      ...base,
      color: '#64748b',
      fontSize: '13px',
      fontWeight: 600,
    }),
    singleValue: (base: any) => ({
      ...base,
      color: '#0f172a',
      fontSize: '13px',
      fontWeight: 700,
    }),
    option: (base: any, state: any) => ({
      ...base,
      backgroundColor: state.isFocused ? '#f0fdf4' : '#fff',
      color: '#0f172a',
      fontSize: '13px',
      fontWeight: state.isSelected ? 700 : 600,
    }),
    indicatorSeparator: (base: any) => ({
      ...base,
      backgroundColor: '#e2e8f0',
    }),
  };

  useEffect(() => {
    const fetchJobFilters = async () => {
      const response = await getJobFilters();
      const responseData = await response.json();
      const formattedLocations = responseData.location.map((item: FilterOption | string) =>
        typeof item === 'string' ? { label: item, value: item } : { label: item.label, value: item.value },
      );
      const formattedJobTitles = responseData.jobTitle.map((item: FilterOption | string) =>
        typeof item === 'string' ? { label: item, value: item } : { label: item.label, value: item.value },
      );
      setLocations(formattedLocations);
      setJobTitles(formattedJobTitles);
    };
    getOrganizationTree().then((response) => {
      const departmentTypes = getAllUnitNames(response);
      setDepartmentTypeFilter(departmentTypes);
    });
    fetchJobFilters();
  }, []);

  const getAllUnitNames = (organization: any): SelectOptions[] => {
    const unitNames: SelectOptions[] = [];
    const traverse = (node: any) => {
      unitNames.push({ label: node.unitName, value: node.unitName });
      if (node.children && node.children.length > 0) {
        node.children.forEach((child: any) => traverse(child));
      }
    };
    traverse(organization);
    return unitNames;
  };

  const handleSelectChange = (action: any, actionValues: any) => {
    setFilters({
      ...filters,
      [actionValues.name === 'locationType' ? 'location' : 'jobTitle']: [action.value],
    });
  };

  const handleWorkType = (selectedOption: SelectOptions) => {
    setFilters((prev) => ({
      ...prev,
      workType: [...prev.workType, selectedOption.value as string],
    }));
  };

  const handleDepartment = (selectedOption: SelectOptions) => {
    setFilters((prev) => ({
      ...prev,
      department: [...prev.department, selectedOption.value],
    }));
  };

  const handleClearFilters = () => {
    setFilters({ jobTitle: [], location: [], workType: [], department: [] });
    setSearch(!search);
  };

  return (
    <div className='bg-white p-4'>
      <div className='mb-3 flex items-center justify-between'>
        <p className='text-xs font-bold uppercase text-slate-500'>
          Refine roles
        </p>
        { search && (
          <button
              onClick={handleClearFilters}
              className='text-xs font-bold text-slate-500 transition hover:text-emerald-700'
          >
            { t(JOB_PORTAL_KEYS.filter.clearFilter) }
          </button>
        ) }
      </div>
      <div className='flex flex-col gap-3'>
        <div className='w-full'>
          <Select
              placeholder={t(JOB_PORTAL_KEYS.filter.searchByJobTitle)}
              name='jobTitleType'
              options={jobTitles}
              onChange={handleSelectChange}
              styles={selectStyles}
              isClearable={false}
              closeMenuOnSelect={true}
              hideSelectedOptions={false}
              isMulti={false}
          />
        </div>

        <div className='w-full'>
          <Select
              placeholder={t(JOB_PORTAL_KEYS.filter.searchByLocation)}
              name='locationType'
              options={locations}
              onChange={handleSelectChange}
              styles={selectStyles}
              isClearable={false}
              closeMenuOnSelect={true}
              hideSelectedOptions={false}
              isMulti={false}
          />
        </div>

        <div className='w-full'>
          <CustomSelect
              name='workType'
              value={{ label: t(JOB_PORTAL_KEYS.filter.selectWorkType), value: 'Select Work Type' }}
              options={workTypes}
              onChange={handleWorkType}
          />
        </div>

        <div className='w-full'>
          <CustomSelect
              name='department'
              value={{ label: t(JOB_PORTAL_KEYS.filter.selectDepartment), value: 'Select Department' }}
              options={departmentTypeFilter}
              onChange={handleDepartment}
          />
        </div>

        <Button
            label={t(JOB_PORTAL_KEYS.filter.search)}
            hasIcon={false}
            onClick={() => setSearch(!search)}
            variant='primary'
            className='mt-1 w-full rounded-lg'
        />
      </div>
    </div>
  );
};

export default JobPostFilter;
